// Mobile-mode route service — wraps server endpoints for the technician route view.
// Aligned with jobviousServer/docs/MOBILE_LOCATION_SERVER_SPEC.md.
//
// Single layer between RouteView/RouteMapView/TodayMapView and the API. Callers
// should never hit /installer-locations/.../route or state-transition endpoints
// directly — go through here so idempotency keys and error normalization stay
// consistent.

import { HTTPService, generateIdempotencyKey } from './httpService';

const API = new HTTPService();

/**
 * Fetch the sequenced route for a mobile location on a given date.
 *
 * Response shape (confirmed against MOBILE_LOCATION_SERVER_SPEC.md 2026-05-12):
 * {
 *   location: { location_id, installer_type, timezone, base_address, ... },
 *   date: 'YYYY-MM-DD',                              // echoed request param
 *   stops: [
 *     {
 *       request_service_id, request_id,
 *       sequence,                                    // 1-indexed integer
 *       customer: { name, phone, lat, lng, address }, // lat/lng nullable
 *       vehicle_label,
 *       start_time: 'HH:MM',                         // 24h local, location TZ
 *       end_time:   'HH:MM',
 *       travel_minutes_in:    INT,                    // base→first for stop #1
 *       travel_distance_miles: DECIMAL(6,2),
 *       state: {
 *         service_state,                              // broader workflow enum
 *         started_travel_at,  started_travel_by,
 *         arrived_at,         arrived_by,
 *         started_at,         finished_at
 *       },
 *       services: [...]
 *     }
 *   ],
 *   day_summary: { total_miles, total_drive_minutes, stops_remaining }
 *   // No polyline in v1 — client calls Google Directions JS API with stop coords.
 * }
 *
 * Display state of each stop derived client-side via
 * `deriveStopDisplayState(stop.state)` (src/util/routeStopState.js).
 */
export const getRoute = async (locationId, date) => {
  const { data } = await API.get(`/installer-locations/${locationId}/route`, { date });
  return data;
};

/**
 * Fan out to fetch routes for multiple mobile locations on a given date.
 * Used by the Today-View Map sub-view (admin dispatch map across all crews).
 *
 * Returns an array of `{ locationId, route, error }`. Failures are captured
 * per-location rather than rejecting the whole batch — one crew's missing
 * route shouldn't blank the entire dispatch map.
 *
 * When the server adds a batch endpoint (e.g. GET /installer-locations/routes
 * ?date=&location_ids=...), swap this implementation; callers stay unchanged.
 */
export const getRoutesForLocations = async (locationIds, date) => {
  const results = await Promise.allSettled(
    (locationIds || []).map(id => getRoute(id, date))
  );
  return results.map((result, idx) => ({
    locationId: locationIds[idx],
    route: result.status === 'fulfilled' ? result.value : null,
    error: result.status === 'rejected' ? result.reason : null,
  }));
};

/**
 * Fetch available appointment start slots for a mobile location on a given
 * date, accounting for the existing route + travel to/from this customer.
 *
 * Expected response shape (per server spec — endpoint pending):
 * {
 *   date: 'YYYY-MM-DD',
 *   duration_minutes: 150,
 *   slots: [{ start_time: 'HH:MM', end_time: 'HH:MM', travel_minutes_in?: N, can_fit: true }, ...],
 *   operating_hours: { open: 'HH:MM', close: 'HH:MM' },  // for force-book grid
 *   closed_reason?: 'holiday' | 'blocked' | 'closed'      // optional, when no slots
 * }
 *
 * Caller resolves customerLat/Lng from the customer record or the just-typed
 * Place result. Service duration in minutes is summed from the booking's
 * services.
 */
export const getMobileAvailability = async ({
  locationId, date, durationMinutes, customerLat, customerLng, excludeRequestId,
}) => {
  const params = {
    date,
    durationMinutes,
    customerLat,
    customerLng,
  };
  // Reschedule context — exclude the booking being modified from conflict
  // detection so it doesn't block itself.
  if (excludeRequestId != null) params.excludeRequestId = excludeRequestId;
  const { data } = await API.get(`/availability/mobile/${locationId}`, params);
  return data;
};

/**
 * Send a delay-SMS to the customer for a slipping stop.
 * Server records the notification + updated ETA.
 */
export const notifyDelay = async (requestServiceId, newEtaIso) => {
  const { data } = await API.post(
    `/request-services/${requestServiceId}/notify-delay`,
    { new_eta: newEtaIso },
    { idempotencyKey: generateIdempotencyKey() }
  );
  return data;
};

/**
 * State-transition PATCH for a route stop.
 *
 * @param {number} requestServiceId
 * @param {'started_travel_at'|'arrived_at'|'started_at'|'finished_at'} transition
 * @param {Object} options - { idempotencyKey?, position?, occurredAt? }
 *   - idempotencyKey: pass the same key on retry; new key per distinct action.
 *   - position: { lat, lng, accuracy } when the transition came from geolocation.
 *   - occurredAt: ISO timestamp if client knows it (e.g. when auto-firing on
 *     arrival, the moment of the geofence match rather than the network call).
 */
export const recordTransition = async (requestServiceId, transition, options = {}) => {
  const payload = { transition };
  if (options.position) payload.position = options.position;
  if (options.occurredAt) payload.occurred_at = options.occurredAt;
  // force_complete bypasses the server's task-completion gate on finished_at.
  // Use only after the user has explicitly acknowledged incomplete items via
  // ItemCompletionGuardModal.
  if (options.forceComplete) payload.force_complete = true;

  const idempotencyKey = options.idempotencyKey || generateIdempotencyKey();

  const { data } = await API.patch(
    `/request-services/${requestServiceId}/transition`,
    payload,
    { idempotencyKey }
  );
  return data;
};

// Convenience helpers — each wraps recordTransition with the right verb.
// Callers should generate one idempotency key per user action and pass it on
// any retries so the server can dedupe.
export const startTravel = (requestServiceId, opts) =>
  recordTransition(requestServiceId, 'started_travel_at', opts);

export const recordArrival = (requestServiceId, opts) =>
  recordTransition(requestServiceId, 'arrived_at', opts);

export const startService = (requestServiceId, opts) =>
  recordTransition(requestServiceId, 'started_at', opts);

export const finishService = (requestServiceId, opts) =>
  recordTransition(requestServiceId, 'finished_at', opts);

const RouteService = {
  getRoute,
  getRoutesForLocations,
  getMobileAvailability,
  notifyDelay,
  recordTransition,
  startTravel,
  recordArrival,
  startService,
  finishService,
};

export default RouteService;
